import { useTracking, getText } from '../utils';
import { forwardRef } from 'react';
import { EllipsisVertical } from '@gjensidige/builders-icons';
import { AnimatedArrow } from '../animated-arrow';
import { Loader } from '../loader/loader';
import type {
  PolymorphicComponentPropWithRef,
  PolymorphicRef,
  TrackingProp
} from '../types';
import styles from './button.module.css';
import clsx from 'clsx';

type ButtonBaseProps<Href> = {
  arrow?: boolean | 'left' | 'right';
  disabled?: boolean;
  href?: Href;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  pressed?: boolean;
  tracking?: TrackingProp;
} & (
  | {
      fill?: never;
      flexible?: never; // Deprecated name of "fill"
      loading?: never;
      size?: never;
      variant: 'icon';
    }
  | {
      fill?: boolean;
      flexible?: boolean; // Deprecated name of "fill"
      loading?: boolean;
      size?: 'sm' | 'md';
      variant?: 'action' | 'critical' | 'primary' | 'secondary' | 'unset';
    }
);

export type ButtonProps<
  Href,
  As extends React.ElementType = Href extends string ? 'a' : 'button'
> = PolymorphicComponentPropWithRef<As, ButtonBaseProps<Href>>;

type ButtonComponent = <
  Href,
  As extends React.ElementType = Href extends string ? 'a' : 'button'
>(
  props: ButtonProps<Href, As>
) => JSX.Element;

const loader = (
  <Loader
    aria-label="Loading" // button doesn't have focus when loading so this isn't read, but aria-label is required in Loader
    className={styles.loader}
    size="sm"
    variant="ellipsis"
  />
);

export const Button: ButtonComponent = forwardRef(function Button<
  Href,
  As extends React.ElementType = Href extends string ? 'a' : 'button'
>(
  {
    arrow,
    children,
    className,
    disabled = false,
    fill = false,
    flexible = false,
    icon,
    iconPosition = 'left',
    loading = false,
    onClick,
    pressed = false,
    as,
    size = 'md',
    variant: variantRaw = 'primary',
    tracking = true,
    ...rest
  }: ButtonProps<Href, As>,
  ref: PolymorphicRef<As>
) {
  const Tag = as || (rest.href ? 'a' : 'button');
  const variant = variantRaw.split('-')[0]; // Previously we supported "action-nopadding", so we strip everything after - for backwards compability
  const isAction = variant === 'action';
  const isPrevent = disabled || loading;
  const classes = clsx(
    styles.button,
    styles[variant],
    styles[size],
    className,
    !!(flexible || fill) && styles.fill,
    pressed && styles.pressed
  );

  const track = useTracking(tracking);
  const handleClick = (event: React.MouseEvent<As>) => {
    if (isPrevent) return event.preventDefault();
    track({
      eventCategory: `button_${variant}`,
      eventAction: 'click',
      eventLabel: getText(event.currentTarget)
    });
    onClick?.(event); // Track before onClick in case URL changes
  };
  let left: React.ReactNode = null;
  let right: React.ReactNode = null;

  // Add arrows and ensure icon + arrow at same position can not happen
  if (arrow === 'left')
    left = <AnimatedArrow direction="left" className={styles.arrow} />;
  else if (arrow || (isAction && !icon))
    right = <AnimatedArrow direction="right" className={styles.arrow} />;
  if (icon && iconPosition === 'left') left = icon;
  if (icon && iconPosition === 'right') right = icon;

  // Action button changes icon when loading
  if (isAction && loading) {
    if (left) left = loader;
    else if (right) right = loader;
  }

  // Augment rest parameters
  if (!rest.href) Object.assign(rest, { href: undefined }); // Prevent empty href
  if (Tag === 'button') Object.assign(rest, { type: rest.type || Tag }); // Ensure button type
  if (isPrevent)
    Object.assign(rest, {
      [Tag === 'button' ? 'disabled' : 'aria-disabled']: true,
      'aria-busy': loading || undefined,
      tabIndex: -1
    });

  if (variant === 'icon') {
    if (!left && !right) left = <EllipsisVertical />; // Ensure there is always an icon
    if (children)
      console.warn(`<Button variant="icon"> does not support 'children'`);
  }

  return (
    <Tag className={classes} onClick={handleClick} ref={ref} {...rest}>
      {loading && !isAction && loader}
      {left}
      <span className={styles.content}>{variant !== 'icon' && children}</span>
      {right}
    </Tag>
  );
}) as ButtonComponent; // Needed to tell Typescript this does not return ReactNode but acutally JSX.Element
